import { ENTRY_INTENT_KIND, intentHasGroupDestination } from '../../onboarding/types/entryIntent.types';
import type { EntryIntent } from '../../onboarding/types/entryIntent.types';
import { buildPublicSignInHref } from '../../signIn/helpers/postAuthRedirect.helper';
import { WORLD_CUP_COMPETITION_ID } from '../../worldcup/worldcup.constants';
import {
  competitionBasePath,
  competitionGroupRoute,
  competitionGroupJoinRoute,
} from '../../competitions/helpers/competitionRoutes.helper';

/**
 * Picks the post-tutorial sign-up destination based on entry intent.
 * Always routes to sign-up (signup=true) since this is called after first-run tutorial.
 *
 * - org/squad-invite with a known groupId → sign up then land on that squad page
 * - org/squad-invite with only an invite code → sign up then land on that competition's join screen
 * - returnUrl provided → sign up then return to that path
 * - cold/guest/null → sign up at default landing
 */
export function buildPostTutorialSignInHref(intent: EntryIntent | null, returnUrl?: string): string {
  // An invite carries its own competition; an intent written before competition-aware
  // invites carries none and is a World Cup join.
  const basePath = competitionBasePath(
    (intent as { competitionId?: string } | null)?.competitionId || WORLD_CUP_COMPETITION_ID,
  );
  if (intentHasGroupDestination(intent)) {
    return buildPublicSignInHref({
      signup: true,
      returnUrl: competitionGroupRoute(basePath, intent.groupId),
    });
  }
  if (intent && (intent.kind === ENTRY_INTENT_KIND.ORG || intent.kind === ENTRY_INTENT_KIND.SQUAD_INVITE)) {
    const inviteCode = (intent as { inviteCode?: string }).inviteCode;
    if (inviteCode) {
      return buildPublicSignInHref({
        signup: true,
        returnUrl: competitionGroupJoinRoute(basePath, { code: inviteCode.toUpperCase() }),
      });
    }
  }
  if (returnUrl) {
    return buildPublicSignInHref({ signup: true, returnUrl });
  }
  return buildPublicSignInHref({ signup: true });
}
